import { Field, type InjectedFormikProps } from "formik";
import { useCallback, useContext } from "react";

import { BTCheckbox, BTTextArea } from "@btnet/core-components";

import { BdsButton } from "@buildertrend/components";

import { BuilderInfoContext } from "helpers/globalContext/BuilderInfoContext";
import { usePermission } from "helpers/globalContext/usePermission";

import { routesWebforms } from "utilities/routesWebforms";

import { BTCol } from "commonComponents/btWrappers/BTCol/BTCol";
import { BTForm, BTFormItem, BTFormItemAutomatic } from "commonComponents/btWrappers/BTForm/BTForm";
import { BTIconLegacyTrash } from "commonComponents/btWrappers/BTIcon";
import { BTInputCopy } from "commonComponents/btWrappers/BTInputCopy/BTInputCopy";
import {
    BTInputNumber,
    defaultAntStyle,
} from "commonComponents/btWrappers/BTInputNumber/BTInputNumber";
import { BTRow } from "commonComponents/btWrappers/BTRow/BTRow";
import { BTSelect } from "commonComponents/btWrappers/BTSelect/BTSelect";
import { BTTitle } from "commonComponents/btWrappers/BTTitle/BTTitle";
import { CustomFieldContainer } from "commonComponents/entity/customField/CustomFieldContainer/CustomFieldContainer";
import { Currency } from "commonComponents/financial/Currency/Currency";
import { PageSection } from "commonComponents/utilities/PageSection/PageSection";

import { type IInternalUserHandler } from "entity/internalUser/InternalUserDetails/InternalUser.api.handler";
import {
    FeedType,
    type IInternalUserFormValues,
    type InternalUserEntity,
    type InternalUserFormActions,
} from "entity/internalUser/InternalUserDetails/InternalUser.api.types";

interface IInternalUserOtherProps {
    internalUserEntity: InternalUserEntity;
    isSelf: boolean;
    canEdit: boolean;
    handler?: IInternalUserHandler;
    onAddUpdateCalendarFeed: (
        id: number,
        feedType: FeedType,
        options: number[],
        hasFeed: boolean,
        setFieldValue: (field: string, value: any) => void,
        feedId?: number,
    ) => void;
    onDeleteCalendarFeed: (
        id: number,
        feedType: FeedType,
        setFieldValue: (field: string, value: any) => void,
        feedId?: number,
    ) => void;
    onSubmit: (e?: React.FormEvent<HTMLFormElement>) => void;
    actionBeingPerformed: InternalUserFormActions;
    inEditMode: boolean;
}

export function InternalUserOther(
    props: InjectedFormikProps<IInternalUserOtherProps, IInternalUserFormValues>,
) {
    const {
        internalUserEntity,
        values,
        isSelf,
        canEdit,
        handleChange,
        handleBlur,
        setFieldValue,
        setFieldTouched,
        onAddUpdateCalendarFeed,
        inEditMode,
    } = props;
    const { internalUser } = internalUserEntity;
    const builderInfo = useContext(BuilderInfoContext);
    const isDisabledUnlessSelf = !canEdit && !isSelf;
    const hasTimeClockSection =
        internalUserEntity.canView || internalUserEntity.hasTimeClockCostViewing;
    const isNew = internalUser.id === 0;
    const canEditLaborCost =
        (isNew && internalUserEntity.canAdd) || (!isNew && internalUserEntity.canEdit);
    const canViewTodos = usePermission("toDos", "canView");

    const feedHasUrl = useCallback(
        (feedType: FeedType) => {
            switch (feedType) {
                case FeedType.ScheduleItems:
                    return (values.scheduleItemsFeed?.feedUrl ?? "") !== "";
                case FeedType.LeadActivities:
                    return (values.leadActivitesFeed?.feedUrl ?? "") !== "";
                case FeedType.Todos:
                    return (values.todosFeed?.feedUrl ?? "") !== "";
            }
        },
        [values.scheduleItemsFeed, values.leadActivitesFeed, values.todosFeed],
    );

    const getFeedId = useCallback(
        (feedType: FeedType) => {
            switch (feedType) {
                case FeedType.ScheduleItems:
                    return values.scheduleItemsFeed?.feedId;
                case FeedType.LeadActivities:
                    return values.leadActivitesFeed?.feedId;
                case FeedType.Todos:
                    return values.todosFeed?.feedId;
            }
        },
        [values.scheduleItemsFeed, values.leadActivitesFeed, values.todosFeed],
    );

    const handleAddUpdateCalendarFeed = useCallback(
        async (feedType: FeedType) => {
            let valueItems: number[];
            switch (feedType) {
                case FeedType.ScheduleItems:
                    valueItems = values.scheduleItems;
                    break;
                case FeedType.LeadActivities:
                    valueItems = values.leadActivities;
                    break;
                case FeedType.Todos:
                    valueItems = values.todos;
                    break;
            }

            onAddUpdateCalendarFeed(
                internalUserEntity.internalUser.id,
                feedType,
                valueItems,
                feedHasUrl(feedType),
                setFieldValue,
                getFeedId(feedType),
            );
        },
        [
            feedHasUrl,
            getFeedId,
            internalUserEntity.internalUser.id,
            onAddUpdateCalendarFeed,
            setFieldValue,
            values.leadActivities,
            values.scheduleItems,
            values.todos,
        ],
    );

    return (
        <PageSection title="Other">
            <BTForm className="bds-col bds-gap-2">
                <div>
                    <BTTitle>Schedule</BTTitle>
                    <BTRow align="middle" gutter={8}>
                        <BTCol>
                            <BTFormItemAutomatic<IInternalUserFormValues> id="enableConflictAlert">
                                <BTCheckbox
                                    id="enableConflictAlert"
                                    data-testid="enableConflictAlert"
                                    onChange={handleChange}
                                    checked={values.enableConflictAlert}
                                    disabled={isDisabledUnlessSelf || !inEditMode}
                                >
                                    Notify me when
                                </BTCheckbox>
                            </BTFormItemAutomatic>
                        </BTCol>
                        <BTCol>
                            <BTFormItemAutomatic<IInternalUserFormValues> id="conflictAlertMaxCount">
                                <BTInputNumber
                                    id="conflictAlertMaxCount"
                                    data-testid="conflictAlertMaxCount"
                                    value={values.conflictAlertMaxCount}
                                    style={defaultAntStyle}
                                    onChange={setFieldValue}
                                    onBlur={handleBlur}
                                    min={2}
                                    max={100}
                                    roundToDecimalPlaces={0}
                                    disabled={
                                        !values.enableConflictAlert ||
                                        isDisabledUnlessSelf ||
                                        !inEditMode
                                    }
                                />
                            </BTFormItemAutomatic>
                        </BTCol>
                        <BTCol className="margin-bottom-sm">
                            or more schedule items conflict (minimum 2).
                        </BTCol>
                    </BTRow>
                </div>
                {internalUser.id > 0 && internalUser.calendarFeedOptions && (
                    <div className="bds-col bds-gap-1">
                        <div>
                            <BTTitle>External Calendar Integrations</BTTitle>
                            <a
                                rel="noopener noreferrer"
                                target="_blank"
                                href={routesWebforms.Help.GetExternalCalendarIntegrationLink()}
                            >
                                Learn how to create a feed for your iCal, Google Calendar, or
                                Outlook
                            </a>
                        </div>

                        <BTFormItemAutomatic<IInternalUserFormValues> id="scheduleItems" useBds>
                            <BTSelect
                                id="scheduleItems"
                                data-testid="scheduleItems"
                                className="margin-bottom-sm"
                                style={{ minWidth: "230px" }}
                                multiple
                                notVisiblePlaceholder="-- All Schedule Items Selected --"
                                treeData={internalUser.calendarFeedOptions.scheduleFeedOptions}
                                value={values.scheduleItems}
                                onChange={setFieldValue}
                                onBlur={handleBlur}
                                disabled={!inEditMode}
                            />
                        </BTFormItemAutomatic>
                        <BdsButton
                            id="schedule-items-create-feed"
                            testid="schedule-items-create-feed"
                            onClick={() => handleAddUpdateCalendarFeed(FeedType.ScheduleItems)}
                            disabled={!inEditMode}
                            text={`${
                                feedHasUrl(FeedType.ScheduleItems) ? "Update" : "Create"
                            } Feed`}
                            displayType="secondary"
                            className="margin-bottom-md"
                        />
                        {feedHasUrl(FeedType.ScheduleItems) && (
                            <div className="flex flex-row">
                                <div className="flex flex-col">
                                    <BTFormItemAutomatic<IInternalUserFormValues>
                                        id="scheduleItemsFeed"
                                        useBds
                                    >
                                        <BTInputCopy
                                            id="scheduleItemsFeed"
                                            data-testid="scheduleItemsFeed"
                                            disabled
                                            onChange={setFieldValue}
                                            value={values.scheduleItemsFeed?.feedUrl ?? undefined}
                                            onBlur={setFieldTouched}
                                        />
                                    </BTFormItemAutomatic>
                                </div>
                                <div className="flex flex-col">
                                    <BdsButton
                                        testid="remove-feed"
                                        className="margin-left-sm"
                                        icon={<BTIconLegacyTrash />}
                                        displayType="tertiary"
                                        ariaLabel="Trash"
                                        onClick={() =>
                                            props.onDeleteCalendarFeed(
                                                internalUser.id,
                                                FeedType.ScheduleItems,
                                                setFieldValue,
                                                getFeedId(FeedType.ScheduleItems),
                                            )
                                        }
                                    />
                                </div>
                            </div>
                        )}
                        <BTFormItemAutomatic<IInternalUserFormValues> id="leadActivities" useBds>
                            <BTSelect
                                id="leadActivities"
                                data-testid="leadActivities"
                                className="margin-bottom-sm"
                                style={{ minWidth: "230px" }}
                                multiple
                                notVisiblePlaceholder="-- All Lead Activities Selected --"
                                treeData={
                                    internalUser.calendarFeedOptions.leadActivitiesFeedOptions
                                }
                                value={values.leadActivities}
                                onChange={setFieldValue}
                                onBlur={handleBlur}
                                disabled={!inEditMode}
                            />
                        </BTFormItemAutomatic>
                        <BdsButton
                            id="lead-activities-create-feed"
                            testid="lead-activities-create-feed"
                            onClick={() => handleAddUpdateCalendarFeed(FeedType.LeadActivities)}
                            disabled={!inEditMode}
                            text={`${
                                feedHasUrl(FeedType.LeadActivities) ? "Update" : "Create"
                            } Feed`}
                            displayType="secondary"
                        />
                        {feedHasUrl(FeedType.LeadActivities) && (
                            <div className="flex flex-row margin-bottom-md">
                                <div className="flex flex-col">
                                    <BTFormItemAutomatic<IInternalUserFormValues>
                                        id="leadActivitesFeed"
                                        useBds
                                    >
                                        <BTInputCopy
                                            id="leadActivitesFeed"
                                            data-testid="leadActivitesFeed"
                                            disabled
                                            onChange={setFieldValue}
                                            value={values.leadActivitesFeed?.feedUrl ?? undefined}
                                            onBlur={setFieldTouched}
                                        />
                                    </BTFormItemAutomatic>
                                </div>
                                <div className="flex flex-col">
                                    <BdsButton
                                        testid="remove-feed"
                                        className="margin-left-sm"
                                        onClick={() =>
                                            props.onDeleteCalendarFeed(
                                                internalUser.id,
                                                FeedType.LeadActivities,
                                                setFieldValue,
                                                getFeedId(FeedType.LeadActivities),
                                            )
                                        }
                                        icon={<BTIconLegacyTrash />}
                                        displayType="tertiary"
                                        ariaLabel="Trash"
                                    />
                                </div>
                            </div>
                        )}

                        {canViewTodos && (
                            <>
                                <BTFormItemAutomatic<IInternalUserFormValues> id="todos" useBds>
                                    <BTSelect
                                        id="todos"
                                        data-testid="todos"
                                        className="margin-bottom-sm"
                                        style={{ minWidth: "230px" }}
                                        multiple
                                        notVisiblePlaceholder={"-- All ToDo's Selected --"}
                                        treeData={internalUser.calendarFeedOptions.todoFeedOptions}
                                        value={values.todos}
                                        onChange={setFieldValue}
                                        onBlur={handleBlur}
                                        disabled={!inEditMode}
                                    />
                                </BTFormItemAutomatic>
                                <BdsButton
                                    id="todos-create-feed"
                                    testid="todos-create-feed"
                                    onClick={() => handleAddUpdateCalendarFeed(FeedType.Todos)}
                                    disabled={!inEditMode}
                                    text={`${
                                        feedHasUrl(FeedType.Todos) ? "Update" : "Create"
                                    } Feed`}
                                    displayType="secondary"
                                />
                            </>
                        )}
                        {canViewTodos && feedHasUrl(FeedType.Todos) && (
                            <div className="flex flex-row">
                                <div className="flex flex-col">
                                    <BTFormItemAutomatic<IInternalUserFormValues> id="todosFeed">
                                        <BTInputCopy
                                            id="todosFeed"
                                            data-testid="todosFeed"
                                            disabled
                                            onChange={setFieldValue}
                                            value={values.todosFeed?.feedUrl ?? undefined}
                                            onBlur={setFieldTouched}
                                        />
                                    </BTFormItemAutomatic>
                                </div>
                                <div className="flex flex-col">
                                    <BdsButton
                                        testid="remove-feed"
                                        className="margin-left-sm"
                                        onClick={() =>
                                            props.onDeleteCalendarFeed(
                                                internalUser.id,
                                                FeedType.Todos,
                                                setFieldValue,
                                                getFeedId(FeedType.Todos),
                                            )
                                        }
                                        icon={<BTIconLegacyTrash />}
                                        displayType="tertiary"
                                        ariaLabel="Trash"
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                )}
                {hasTimeClockSection && (
                    <div className="bds-col bds-gap-2">
                        <BTTitle>Time Clock</BTTitle>
                        {internalUserEntity.canView && (
                            <BTFormItemAutomatic<IInternalUserFormValues> id="geofencing" useBds>
                                <BTCheckbox
                                    id="geofencing"
                                    data-testid="geofencing"
                                    onChange={handleChange}
                                    checked={values.geofencing}
                                    disabled={!canEdit || !inEditMode}
                                >
                                    Require location on mobile and track when{" "}
                                    {internalUser.firstName} clocks in or out beyond the geofence.
                                </BTCheckbox>
                            </BTFormItemAutomatic>
                        )}
                        {internalUserEntity.canView && internalUserEntity.hasCostCodesFeature && (
                            <BTFormItemAutomatic<IInternalUserFormValues>
                                id="defaultCostCodeId"
                                useBds
                            >
                                <BTSelect
                                    id="defaultCostCodeId"
                                    data-testid="defaultCostCodeId"
                                    treeData={internalUser.defaultCostCodes}
                                    onChange={setFieldValue}
                                    onBlur={handleBlur}
                                    value={values.defaultCostCodeId}
                                    disabled={!canEdit || !inEditMode}
                                />
                            </BTFormItemAutomatic>
                        )}
                        {internalUserEntity.hasTimeClockCostViewing &&
                            internalUserEntity.hasCostCodesFeature && (
                                <BTFormItemAutomatic<IInternalUserFormValues>
                                    id="defaultLaborCost"
                                    useBds
                                >
                                    <Currency
                                        onChange={setFieldValue}
                                        onBlur={handleBlur}
                                        data-testid="defaultLaborCost"
                                        id="defaultLaborCost"
                                        value={values.defaultLaborCost}
                                        disabled={
                                            internalUser.byCostCodeHourlyTracking ||
                                            !canEditLaborCost ||
                                            !canEdit ||
                                            !inEditMode
                                        }
                                    />
                                </BTFormItemAutomatic>
                            )}
                        {internalUserEntity.hasTimeClockCostViewing &&
                            builderInfo?.flags.timeClockBillRate && (
                                <BTFormItemAutomatic<IInternalUserFormValues>
                                    id="defaultBillRate"
                                    useBds
                                    help="Used as the default billable rate when no job-specific rate is set"
                                >
                                    <Currency
                                        onChange={setFieldValue}
                                        onBlur={handleBlur}
                                        data-testid="defaultBillRate"
                                        id="defaultBillRate"
                                        value={values.defaultBillRate}
                                        disabled={!canEditLaborCost || !canEdit || !inEditMode}
                                        showPlaceholder
                                        placeholder=""
                                        allowNegative={false}
                                    />
                                </BTFormItemAutomatic>
                            )}
                    </div>
                )}
                <div>
                    <BTTitle>Contact</BTTitle>
                    <BTFormItemAutomatic<IInternalUserFormValues> id="contactInfo" useBds>
                        <BTTextArea
                            id="contactInfo"
                            data-testid="contactInfo"
                            value={values.contactInfo}
                            onChange={setFieldValue}
                            onBlur={setFieldTouched}
                            disabled={!canEdit || !inEditMode}
                        />
                    </BTFormItemAutomatic>
                </div>
                <BTFormItem>
                    <Field
                        name="customFieldContainer"
                        component={CustomFieldContainer}
                        disabled={!canEdit || !inEditMode}
                        fields={{
                            ...internalUser,
                            customFields: values.customFields,
                            useBds: true,
                        }}
                    />
                </BTFormItem>
            </BTForm>
        </PageSection>
    );
}
